import '@/styles/index.scss'
import '@/styles/main.scss'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'
import App from './App.vue'
import router from './router.ts'
import { createLegacyBridgeGuard } from './legacyBridgeGuard'
import { createOrganizationGateGuard } from './organizationGateGuard'
import { createPermissionGuard } from './permissionGuard'
import { createSessionGuard } from './sessionGuard'
import { retryTransient, setOnCreditsExhausted, setOnNoOrganization, setOnUnauthorized } from '@/api/client'
import { useCreditsExhaustedStore } from '@/apps/billing/stores/creditsExhausted'
import { useSessionStore } from '@/apps/session/stores/session'
import {
  createVuetifyPlugin,
  createI18nPlugin,
  buildLocaleMessages,
  vOverflowTooltip,
} from '@/apps/core'
import { createOrganizationChangeGuard } from '@/apps/assets/composables/useUnsavedGuard'
import { createSettingsPlugin } from '@/apps/settings'
import { videasSettingsRoutes } from '@/apps/settings/routes'
import { videasNavigationSections } from '@/apps/settings/navigation'

// `en*.js` matches both `en.js` (its first-level keys are the app's sections) and
// `en.<section>.js` (one section per file). The staff console's copy is its own entry's, and stays
// out of the customers' bundle.
const messages = {
  en: buildLocaleMessages(
    import.meta.glob(['../../apps/*/locales/en*.js', '!../../apps/staff/**'], { eager: true }),
  ),
  fr: buildLocaleMessages(
    import.meta.glob(['../../apps/*/locales/fr*.js', '!../../apps/staff/**'], { eager: true }),
  ),
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: retryTransient,
    },
  },
})

// Ahead of the session guard, while `X-Org-Id` still names the organization the page shows: a form
// holding changes asks before another organization is entered, and staying leaves the tenant alone.
router.beforeEach(createOrganizationChangeGuard())
router.beforeEach(createSessionGuard({ queryClient }))
// Straight after the session guard, which has set `X-Org-Id` and checked the membership, and
// before anything decides about the organization's screens: one the previous platform still
// serves is left for it rather than opened here.
router.beforeEach(createLegacyBridgeGuard())
router.beforeEach(createPermissionGuard())
// Last: what a route asks of the organization beyond a permission (`meta.gate`), read from the
// answer the session guard fetched for the organization being entered.
router.beforeEach(createOrganizationGateGuard({ queryClient }))

// A 401 while the app believes it is signed in means the session cookie died — expired,
// revoked, or signed out on another device — and every cached read was that session's.
setOnUnauthorized(() => {
  const session = useSessionStore()
  if (!session.isAuthenticated) return
  session.clear()
  queryClient.clear()
  void router.replace({ name: 'session:login', query: { next: router.currentRoute.value.fullPath } })
})

// `403 no_organization` is a state, not a failure: the session is fine, and the way on is to
// pick an organization — or to be invited into one.
setOnNoOrganization(() => {
  void router.replace({ name: 'organizations:list', query: { pick: '1' } })
})

// `402 credits_exhausted` is never a toast: it opens the one modal that says what is missing and
// tops up in place. A caller that may run the act again adds its retry to the same modal.
setOnCreditsExhausted(() => {
  void useCreditsExhaustedStore().report()
})

const settingsPlugin = createSettingsPlugin({
  router,
  features: {
    apiTokens: true,
    profile: true,
    dangerZone: true,
  },
  redirects: {
    backRoute: { name: 'organizations:list' },
  },
  contentMaxWidth: '1200px',
  customRoutes: videasSettingsRoutes,
  customNavigationSections: videasNavigationSections,
  parentRouteName: 'app:layout',
})

const vuetify = createVuetifyPlugin()

// French only for French browsers; any other language defaults to English.
const i18n = createI18nPlugin({ messages, defaultLocale: 'en' })

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(VueQueryPlugin, { queryClient })
app.use(vuetify)
app.use(i18n)
app.directive('overflow-tooltip', vOverflowTooltip)
app.use(settingsPlugin)
app.use(router)

app.mount('#app')
